"use client";

import { cn } from "@/lib/utils";
import React from "react";
import { ButtonStyleType, SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { Button } from "@/components/ui/button";
import Image from '@/components/ui/Image';
import { useEffect, useState } from "react";
import parse from "html-react-parser";
import Link from "next/link";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { Progress } from "@/components/ui/progress";
import { SectionWrapper } from "../components/SectionWraper";

interface GalleryItem {
  image?: string;
  imageAltText?: string;
  title?: string;
  content?: string;
  link?: string;
}

interface Gallery2Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  title?: string;
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  galleryItems?: GalleryItem[];
  attributes?: React.HTMLProps<HTMLElement>;
}

const Gallery2 = ({
  darkMode = false,
  variant = "A",
  spacing = "xlarge",
  usedInPageNav = false,
  pageNavTitle = "Gallery 2",
  themeChangeOnScroll = false,
  title,
  content,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  galleryItems,
  attributes,
}: Gallery2Props) => {
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [progress, setProgress] = useState(Math.floor(100 / (galleryItems?.length ?? 1)));

  useEffect(() => {
    if (!carouselApi) {
      return;
    }
    const updateSelection = () => {
      setCanScrollPrev(carouselApi.canScrollPrev());
      setCanScrollNext(carouselApi.canScrollNext());
    };
    updateSelection();
    carouselApi.on("select", updateSelection);
    return () => {
      carouselApi.off("select", updateSelection);
    };
  }, [carouselApi]);

  useEffect(() => {
    if (!carouselApi) {
      return;
    }
    carouselApi.on("scroll", ({ scrollProgress }) => {
      setProgress(
        Math.max(1 / (galleryItems?.length ?? 1), Math.min(1, scrollProgress())) * 100,
      );
    });
  }, [carouselApi, galleryItems?.length]);

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="gallery2"
      attributes={attributes}
    >   
      <div className="container" data-aos="fade-up">
        <div className="mb-6 gap-10 lg:gap-20 flex flex-col justify-between sm:mb-14 sm:flex-row sm:items-center lg:mb-16">
          <div>
            {title && (
              <h2 className="text-center sm:text-left sm:max-w-[500px] lg:max-w-[800px] mx-auto sm:mx-0">
                {parse(title)}
              </h2>
            )}
            {content && (
              <div className="mt-6 paragraph-lg text-muted-foreground text-center sm:text-left">
                {parse(content)}
              </div>
            )}
            {hasButton && (
              <>
                <Button asChild variant={buttonStyle} size="lg" className="mt-2 w-full sm:w-auto">
                  <Link href={buttonLink ?? ""}>
                    {buttonText}
                  </Link>
                </Button>
                {microCopy && (
                  <div className="mt-2 paragraph-sm text-muted-foreground text-center sm:text-left">
                    {microCopy}
                  </div>
                )}
              </>
            )}
            
          </div>
          {galleryItems && (
            <div className="shrink-0 items-center justify-end gap-2 sm:mt-0 hidden sm:flex">
              <Button
                size="iconSm"
                variant="outline"
                grayBorder
                onClick={() => {
                  carouselApi?.scrollPrev();
                }}
                disabled={!canScrollPrev}
                className="disabled:pointer-events-auto"
              >
                <span className="material-symbols-outlined">
                  arrow_back  
                </span>
              </Button>
              <Button
                size="iconSm"
                variant="outline"
                grayBorder
                onClick={() => {
                  carouselApi?.scrollNext();
                }}
                disabled={!canScrollNext}
                className="disabled:pointer-events-auto"
              >
                <span className="material-symbols-outlined">
                  arrow_forward
                </span>
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className="relative w-full overflow-hidden mt-10 sm:mt-0">
        <Carousel
          setApi={setCarouselApi}
          opts={{
            align: "start",
            breakpoints: {
              "(max-width: 768px)": {
                dragFree: true,
              },
            },
          }}
          data-aos="fade-left"
          data-aos-delay={100}
        >
          <CarouselContent 
            className={cn(
              "mr-4 ml-0",
              "md:mr-8 md:ml-4",
              "2xl:mr-[max(0rem,calc(100vw/2-1336px/2))] 2xl:ml-[max(0rem,calc(100vw/2-1336px/2-20px))]"
            )}
          >
            {galleryItems?.map((item, index) => (
              <CarouselItem 
                key={index} 
                className="pl-4 md:pl-5 max-w-[344px]"
              >
                <div className="group flex flex-col justify-between">
                  <div className="flex aspect-[324/416] overflow-clip rounded-xl">
                    <div className="flex-1">
                      <a href={item.link ?? ""} className="group">
                        <div className="relative h-full w-full origin-bottom">
                          {item.image && (
                            <Image
                              src={item.image}  
                              alt={item.imageAltText ?? ""}
                              title={item.imageAltText ?? ""}
                              className="h-full w-full object-cover object-center group-hover:scale-105 !transition-transform duration-300 ease-in-out"
                              fill
                              sizes="600px"
                            />
                          )}
                          <div className="absolute bottom-2 left-2 right-2 p-4 bg-background rounded-xl flex flex-col gap-4 group-hover:bg-primary">
                            <h5 className="text-foreground group-hover:text-primary-foreground">
                              {item.title}
                            </h5>
                            <Button variant="outline" lifted grayBorder size="iconLg" className="pointer-events-none group-hover:pointer-events-auto group-hover:text-primary-foreground">
                              <span className="material-symbols-outlined ">
                                arrow_forward
                              </span>
                            </Button>
                          </div>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
                
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="container mt-6 md:mt-8">
            <Progress value={progress} className="h-[2px] w-full max-w-[300px]" />
          </div>
        </Carousel>
      </div>
    </SectionWrapper>
  );
};

export { Gallery2 };



